import { useConfiguratorContext } from "@studio3dx/legacy-use-configurator/react";
import { ConfiguratorSectionProps } from "../RenderCustomComponent";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { Check, Flame } from "lucide-react";
import Image from "next/image";
import { useTheme } from "next-themes";

export function BaseVariantSwitcher(props: ConfiguratorSectionProps) {
  const configurator = useConfiguratorContext();

  const [pendingValue, setPendingValue] = useState<string | undefined>(
    undefined
  );
  const [changeConfirmationOpen, setChangeConfirmationOpen] = useState(false);

  const { setTheme } = useTheme();

  const handleValueChange = (value: string) => {
    setPendingValue(value);
    setChangeConfirmationOpen(true);
  };

  const handleChangeConfirmation = () => {
    setChangeConfirmationOpen(false);
    if (!pendingValue) return;
    console.log("pendingValue", pendingValue);
    configurator.selectOption(props.section.id, pendingValue);
  };

  const handleChangeCancel = () => {
    setChangeConfirmationOpen(false);
  };

  const getVariantLogo = (label: string) => {
    const lowerLabel = label.toLowerCase();
    if (
      lowerLabel.includes("abarth") ||
      lowerLabel.includes("sport") ||
      lowerLabel.includes("performance")
    ) {
      return "/images/abarth-scorpion.png";
    }
    return "/images/500-classic.png";
  };

  const currentOption = configurator.getSelectedOptions(props.section.id)[0];

  return (
    <div className="w-full">
      <div className="grid grid-cols-2 gap-3 w-full">
        {configurator.getAllOptions(props.section.id).map((option) => {
          const isSelected = currentOption?.id === option.id;

          return (
            <button
              key={option.id}
              onClick={() => handleValueChange(option.id)}
              className={cn(
                "relative flex items-center justify-center p-8 rounded-lg border-2 transition-all duration-300",
                "hover:scale-105 hover:shadow-lg",
                isSelected
                  ? "border-black bg-gray-50 shadow-lg"
                  : "border-gray-200 bg-white hover:border-gray-300"
              )}
            >
              {/* Selected Check Badge */}
              {isSelected && (
                <div className="absolute -top-2 -right-2 w-7 h-7 bg-black rounded-full flex items-center justify-center shadow-lg animate-in zoom-in duration-200">
                  <Check className="w-4 h-4 text-white" />
                </div>
              )}

              {/* Variant Logo */}
              <div
                className={cn(
                  "transition-all duration-300",
                  isSelected && "scale-105"
                )}
              >
                <Image
                  src={getVariantLogo(option.label)}
                  alt={option.label}
                  width={120}
                  height={120}
                  className="object-contain"
                />
              </div>
            </button>
          );
        })}
      </div>

      <Dialog
        open={changeConfirmationOpen}
        onOpenChange={setChangeConfirmationOpen}
      >
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-xl">
              <Flame className="w-5 h-5 text-red-500" />
              Confirm Variant Change
            </DialogTitle>
          </DialogHeader>
          <DialogDescription className="pt-2 text-base">
            Switching to a different variant will reset all your current
            configuration choices to the default options for that model.
          </DialogDescription>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button variant="outline" onClick={handleChangeCancel}>
              Cancel
            </Button>
            <Button
              onClick={handleChangeConfirmation}
              className="bg-black hover:bg-gray-800"
            >
              Confirm Change
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
